import { useState, useEffect, useCallback } from 'react';

export interface PlaceSuggestion {
	id: string;
	description: string;
	matchedSubstrings: Array<{
		offset: number;
		length: number;
	}>;
	placeId: string;
	terms: Array<{
		offset: number;
		value: string;
	}>;
	types: string[];
}

export interface UsePlacesAutocompleteOptions {
	requestOptions?: {
		componentRestrictions?: {
			country?: string | string[];
		};
		fields?: string[];
	};
	debounce?: number;
	defaultValue?: string;
}

export interface UsePlacesAutocompleteReturn {
	value: string;
	setValue: (value: string, shouldFetch?: boolean) => void;
	suggestions: PlaceSuggestion[];
	ready: boolean;
	clearSuggestions: () => void;
	loading: boolean;
}

export const usePlacesAutocompleteNew = (
	options: UsePlacesAutocompleteOptions = {},
): UsePlacesAutocompleteReturn => {
	const { requestOptions = {}, debounce = 300, defaultValue = '' } = options;

	const [value, setValueState] = useState<string>(defaultValue);
	const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
	const [ready, setReady] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	// Check if Google Maps is loaded
	useEffect(() => {
		const checkGoogleMaps = () => {
			if (window.google && window.google.maps && window.google.maps.places) {
				setReady(true);
			} else {
				// Retry after a short delay
				setTimeout(checkGoogleMaps, 100);
			}
		};

		if (typeof window !== 'undefined') {
			checkGoogleMaps();
		}
	}, []);

	const fetchSuggestions = useCallback(
		async (input: string) => {
			if (!input.trim() || !ready) {
				setSuggestions([]);
				return;
			}

			setLoading(true);

			try {
				// Use AutocompleteService (still available but deprecated)
				// Note: AutocompleteSuggestion is not yet available in current Google Maps types
				const service = new google.maps.places.AutocompleteService();

				const request: google.maps.places.AutocompletionRequest = {
					input,
				};

				// Add optional properties if they exist
				if (requestOptions.componentRestrictions) {
					request.componentRestrictions =
						requestOptions.componentRestrictions as google.maps.places.ComponentRestrictions;
				}
				// Note: 'fields' property is not supported in AutocompletionRequest

				// Use the callback-based getPlacePredictions method
				service.getPlacePredictions(
					request,
					(
						predictions: google.maps.places.AutocompletePrediction[] | null,
						status: google.maps.places.PlacesServiceStatus,
					) => {
						if (
							status === google.maps.places.PlacesServiceStatus.OK &&
							predictions
						) {
							const transformedSuggestions: PlaceSuggestion[] = predictions.map(
								(prediction) => ({
									id: prediction.place_id,
									description: prediction.description,
									matchedSubstrings: prediction.matched_substrings || [],
									placeId: prediction.place_id,
									terms: prediction.terms || [],
									types: prediction.types || [],
								}),
							);
							setSuggestions(transformedSuggestions);
						} else {
							setSuggestions([]);
						}
						setLoading(false);
					},
				);
				return; // Early return for callback-based API
			} catch (error) {
				console.error('Error fetching autocomplete suggestions:', error);
				setSuggestions([]);
			} finally {
				setLoading(false);
			}
		},
		[ready, requestOptions],
	);

	const debouncedFetch = useCallback(
		(input: string) => {
			if (debounce) {
				setTimeout(() => fetchSuggestions(input), debounce);
			} else {
				fetchSuggestions(input);
			}
		},
		[fetchSuggestions, debounce],
	);

	const setValue = useCallback(
		(newValue: string, shouldFetch = true) => {
			setValueState(newValue);
			if (shouldFetch && newValue) {
				debouncedFetch(newValue);
			} else {
				setSuggestions([]);
			}
		},
		[debouncedFetch],
	);

	const clearSuggestions = useCallback(() => {
		setSuggestions([]);
	}, []);

	// Fetch suggestions when value changes
	useEffect(() => {
		if (value && ready) {
			debouncedFetch(value);
		}
	}, [value, ready, debouncedFetch]);

	return {
		value,
		setValue,
		suggestions,
		ready,
		clearSuggestions,
		loading,
	};
};
